import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useRef, useState } from 'react';
import { Badge, Button, ModalWindow, useLocalizeMessage, useToastNotification } from 'libs.nucleus';
import { buildConsentTemplateSource, compileConsentTemplateSource } from '../bundler';
import { ConsentConfigPanel } from './consent_config_panel.component';
import { ConsentTemplateList } from './consent_template_list.component';
import { AgentResultsParseError } from '../consent_agent/parse_agent_results';
import { useConsentSignatureAgent } from '../consent_agent/use_consent_signature_agent.hook';
import { useConsentActionsContext } from '../hooks/use_consent_context.hook';
import { ConsentPdfEditorContent } from './pdf_editor/consent_pdf_editor.component';
import { validateConsentDocumentForPublish } from '../utils/consent_publish_validation';
export const ConsentEditorModal = ({ isOpen, document, consentFiles, onClose, onSelectTemplate, }) => {
    const translate = useLocalizeMessage();
    const { addNotification } = useToastNotification();
    const { autoMapElements, loadDocuments, saveConsentTemplate, saveDocumentToStorage, publishConsentTemplate } = useConsentActionsContext();
    const { locateSignatureFields } = useConsentSignatureAgent();
    const [templatesSidebarCollapsed, setTemplatesSidebarCollapsed] = useState(false);
    const [isLocating, setIsLocating] = useState(false);
    const [isPublishing, setIsPublishing] = useState(false);
    const locateRequestIdRef = useRef(0);
    const handleLocateSignatureFields = useCallback(async () => {
        if (!document) {
            return;
        }
        const requestId = ++locateRequestIdRef.current;
        const targetDocumentId = document.id;
        setIsLocating(true);
        try {
            const elements = await locateSignatureFields({
                pdfDataUrl: document.pdfDataUrl,
                documentName: document.name,
            });
            if (requestId !== locateRequestIdRef.current) {
                return;
            }
            if (elements.length === 0) {
                addNotification({
                    type: 'info',
                    title: translate('No signature fields found'),
                    subtitle: translate('The analyzer did not return any regions to place.'),
                });
                return;
            }
            autoMapElements(targetDocumentId, elements);
            addNotification({
                type: 'success',
                title: translate('Signature fields added'),
                subtitle: translate('The agent detected and placed {count} field(s).', { count: String(elements.length) }),
            });
        }
        catch (e) {
            if (requestId !== locateRequestIdRef.current) {
                return;
            }
            const message = e instanceof AgentResultsParseError
                ? e.message
                : e instanceof Error
                    ? e.message
                    : translate('Something went wrong.');
            addNotification({
                type: 'error',
                title: translate('Could not locate signature fields'),
                subtitle: message,
            });
        }
        finally {
            if (requestId === locateRequestIdRef.current) {
                setIsLocating(false);
            }
        }
    }, [document, locateSignatureFields, autoMapElements, addNotification, translate]);
    const handlePublish = useCallback(async () => {
        if (!document || isPublishing) {
            return;
        }
        const validationError = validateConsentDocumentForPublish(document);
        if (validationError) {
            addNotification({
                type: 'error',
                title: translate('Publish failed'),
                subtitle: translate(validationError),
            });
            return;
        }
        setIsPublishing(true);
        try {
            const source = buildConsentTemplateSource(document);
            const bundle = compileConsentTemplateSource(source);
            await saveConsentTemplate(document, bundle);
            await publishConsentTemplate(document);
            onClose();
            await loadDocuments();
        }
        catch {
            addNotification({
                type: 'error',
                title: translate('Publish failed'),
                subtitle: translate('Unknown error'),
            });
        }
        finally {
            setIsPublishing(false);
        }
    }, [
        document,
        isPublishing,
        saveConsentTemplate,
        publishConsentTemplate,
        onClose,
        loadDocuments,
        addNotification,
        translate,
    ]);
    const header = document ? (_jsxs("div", { className: 'flex flex-row items-center justify-end gap-2 border-b border-neutral-200 px-4 pb-3 pt-2', children: [_jsx("div", { className: 'flex flex-1 flex-col', children: _jsxs("div", { className: 'flex items-center gap-2', children: [_jsx("h2", { className: 'text-lg font-semibold text-neutral-900', "data-testid": 'modal-document-title', children: document.name }), _jsx(Badge, { variant: 'warning', label: translate('Draft') })] }) }), _jsx(Button, { variant: 'transparent', size: 'sm', label: translate('Locate signature fields'), onClick: () => void handleLocateSignatureFields(), disabled: isLocating, dataTestId: 'consent-editor-modal-locate-signatures' })] })) : null;
    return (_jsx(ModalWindow, { isOpen: isOpen, closeWindow: onClose, header: header, width: 'full', removeBodyPadding: true, dataTestId: 'consent-editor-modal', footerSecondaryActionButtons: [
            { label: translate('Close'), onClick: onClose },
            { label: translate('Save'), onClick: () => document && void saveDocumentToStorage(document) },
        ], footerPrimaryActionButton: {
            label: translate('Publish'),
            onClick: () => void handlePublish(),
            disabled: isPublishing,
        }, children: _jsxs("div", { className: 'flex h-full min-h-0 flex-row overflow-hidden', "data-testid": 'consent-editor-modal-body', children: [_jsx(ConsentTemplateList, { items: consentFiles, activeId: document?.id ?? null, onSelectTemplate: onSelectTemplate, isCollapsed: templatesSidebarCollapsed, onToggleCollapse: () => setTemplatesSidebarCollapsed((prev) => !prev) }), _jsx("div", { className: 'flex min-h-0 flex-1 flex-col overflow-hidden', children: document ? (_jsx(ConsentPdfEditorContent, { document: document })) : (_jsx("div", { className: 'flex flex-1 items-center justify-center py-12 text-sm text-neutral-500', children: translate('Select a template to edit.') })) }), _jsx(ConsentConfigPanel, {})] }) }));
};
